import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { InputProvider, groupInput, input } from "@redotech/ui/form";
import { memo } from "react";
import { OutboundLabelForm } from "../outbound-labels";

export const pointsSettingsForm = groupInput({
  enabled: input<boolean>(),
  uspsLabelPointsAwarded: input<number>(),
  fedexLabelPointsAwarded: input<number>(),
  upsLabelPointsAwarded: input<number>(),
  amazonLabelPointsAwarded: input<number>(),
  firstMileLabelPointsAwarded: input<number>(),
});

export type PointsSettingsForm = InputProvider.Form<typeof pointsSettingsForm>;

export type PointsSettingsValue = InputProvider.Value<
  typeof pointsSettingsForm
>;

export const pointsSettingsDefault: PointsSettingsValue = {
  enabled: false,
  uspsLabelPointsAwarded: 5,
  fedexLabelPointsAwarded: 5,
  upsLabelPointsAwarded: 5,
  amazonLabelPointsAwarded: 5,
  firstMileLabelPointsAwarded: 5,
};

export const PointsProgramSettingsCard = memo(
  function PointsProgramSettingsCard({ input }: { input: OutboundLabelForm }) {
    const { pointsSettings } = input.inputs;
    const enabled = pointsSettings.inputs.enabled.value;

    return (
      <Card title="Redo Points Program" w="full">
        <Flex dir="column" gap="md">
          <Text fontSize="sm" textColor="warning">
            Warning: This feature will credit earned points to the merchant's
            balance at the end of the month. Only enable if you are sure you
            want to do this.
          </Text>
          <Text fontSize="sm" textColor="error">
            Do not enable without talking to an engineer first.
          </Text>
          <FormSwitch
            input={pointsSettings.inputs.enabled}
            label="Enable Redo Points Program"
          />

          <Text fontSize="sm" textColor="secondary">
            Configure points awarded per label for each carrier. One point is
            equal to one cent when redeemed.
          </Text>

          <RedoIncrementDecrement
            disabled={!enabled}
            label="USPS points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.uspsLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.uspsLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="FedEx points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.fedexLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.fedexLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="UPS points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.upsLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.upsLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="Amazon points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.amazonLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.amazonLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="FirstMile points per label"
            max={100}
            min={0}
            setValue={
              pointsSettings.inputs.firstMileLabelPointsAwarded.setValue
            }
            step={1}
            value={pointsSettings.inputs.firstMileLabelPointsAwarded.value}
          />
        </Flex>
      </Card>
    );
  },
);
